/**
 * Job SWMS from the client-layer template (field.swms): the Create SWMS form in the job's Documents section
 * (signed by the person making it, and sent to the client to sign when the template says so), the client
 * sign-off status of each version, the warning listing job fields that printed as "confirm at the toolbox
 * talk", and the Today card for jobs starting soon without a client-signed SWMS.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Icon } from "../../components/Icon";
import { plural } from "../../components/PageHeader";
import { Chip, EmptyState, ErrorState, SkeletonLines } from "../../components/States";
import { BRAND } from "../../lib/config";
import { fmtDay, fmtTime } from "../../lib/format";
import { openBlobInNewTab } from "../../lib/jobs-data";
import { Notice, useNotice } from "../../components/Notice";
import type { JobDetail, JobDocument } from "../../lib/supabase";
import { useAsync } from "../../lib/useAsync";
import { cancelSwmsLink, createSwms, defaultRecipient, executedSwmsPdf, sendSwmsLink, signoffState, unsignedSoon, type SwmsSignoffRow } from "./swms-data";

/** Job fields a SWMS reads, as the API names them in missing_fields. */
export const SWMS_FIELD_LABEL: Record<string, string> = {
  site: "site address",
  client: "client",
  siteContact: "site contact",
  plan: "plan (add the TMP or TGS to the job's documents)",
  permit: "permit reference",
  hours: "approved hours",
  siteControls: "permit conditions or notes for crew (the site-specific controls)",
};

export const swmsTemplates = () => BRAND.field?.swms ?? [];

/** What will print blank, worked out the same way as the API does. */
export function blankSwmsFields(job: JobDetail): string[] {
  const has = (v: string | null | undefined) => Boolean(v?.trim());
  const current = job.job_documents.filter((d) => !d.superseded_by);
  const out: string[] = [];
  if (!has(job.site_address)) out.push("site");
  if (!has(job.client_name)) out.push("client");
  if (!has(job.site_contact_name) && !has(job.site_contact_phone)) out.push("siteContact");
  if (!current.some((d) => d.kind === "tmp" || d.kind === "tgs")) out.push("plan");
  if (!has(job.permit_reference)) out.push("permit");
  if (!has(job.approved_hours)) out.push("hours");
  if (!has(job.permit_conditions) && !has(job.crew_notes)) out.push("siteControls");
  return out;
}

const listText = (keys: string[]) => {
  const labels = keys.map((k) => SWMS_FIELD_LABEL[k] ?? k);
  return labels.length > 1 ? `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}` : labels[0] ?? "";
};

export function CreateSwms({ job, onDone, onCancel }: { job: JobDetail; onDone: (text: string) => void; onCancel: () => void }) {
  const template = swmsTemplates()[0]!;
  const toClient = Boolean(template.clientSignoff);
  const business = BRAND.name;
  const [hospital, setHospital] = useState(job.nearest_hospital ?? "");
  const [ticked, setTicked] = useState<string[]>(template.highRiskWork.filter((h) => h.default).map((h) => h.id));
  const [issuerName, setIssuerName] = useState("");
  const [issued, setIssued] = useState(false);
  const [recipient, setRecipient] = useState({ name: job.site_contact_name ?? "", email: job.site_contact_email ?? "" });
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  const blank = blankSwmsFields(job);
  const replacing = job.job_documents.filter((d) => !d.superseded_by && d.kind === "swms");
  const statement = template.issuedStatement.replace(/\{business\}/g, business);

  useEffect(() => {
    if (!toClient || job.site_contact_email) return;
    let live = true;
    defaultRecipient(job).then((r) => live && setRecipient((cur) => (cur.email ? cur : r))).catch(() => {});
    return () => {
      live = false;
    };
  }, [job, toClient]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (hospital.trim().length < 3) return setNotice({ tone: "error", text: "Enter the nearest hospital." });
    if (issuerName.trim().length < 2 || !issued) return setNotice({ tone: "error", text: `Type your full name and tick "${statement}" to sign the SWMS.` });
    const email = recipient.email.trim();
    if (toClient && email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setNotice({ tone: "error", text: "Check the client's email address, or leave it blank to send later." });
    setBusy(true);
    try {
      const r = await createSwms(job.id, {
        templateId: template.id,
        nearestHospital: hospital.trim(),
        highRiskWork: ticked,
        issuerName: issuerName.trim(),
        issued: true,
        ...(toClient && email ? { recipient: { name: recipient.name.trim(), email } } : {}),
      });
      const crew = `Crew see it in their job pack and sign on to it${r.replaced ? " again" : ""}.`;
      const client = !toClient ? "" : r.sent?.emailed ? ` Sent to ${r.sent.recipientEmail} to sign.` : r.sent ? ` It could not be emailed to the client: ${r.sent.emailError}. Send it again from the job.` : " Not sent to the client yet: send it from the job.";
      onDone(`SWMS ${r.document.versionLabel} made and signed. ${crew}${client}`);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <p className="small">
        Makes the SWMS from your template with this job's details{replacing.length ? `, and replaces ${replacing.map((d) => `${d.title}${d.version_label ? ` (${d.version_label})` : ""}`).join(", ")}. Crew will sign on to the new version` : ""}.
      </p>
      <div className="form-grid">
        <label className="field field--wide">
          <span className="field__label">Nearest hospital</span>
          <input className="input" required value={hospital} onChange={(e) => setHospital(e.target.value)} placeholder="Emergency department and address" />
          <span className="field__hint">Saved on the job too.</span>
        </label>
      </div>
      {template.highRiskWork.length > 0 && (
        <fieldset className="stack" style={{ gap: 6, border: 0, padding: 0, margin: 0 }}>
          <legend className="field__label">High risk construction work on this job</legend>
          {template.highRiskWork.map((h) => (
            <label key={h.id} className="check">
              <input type="checkbox" checked={ticked.includes(h.id)} onChange={(e) => setTicked((t) => (e.target.checked ? [...t, h.id] : t.filter((x) => x !== h.id)))} /> {h.label}
            </label>
          ))}
        </fieldset>
      )}
      {blank.length > 0 && (
        <p className="notice notice--warn small" role="status">
          Not on the job yet: {listText(blank)}. These print as "{template.blankText}". You can still make the SWMS now, or fill them in first.
        </p>
      )}
      {toClient && (
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Send to the client: name</span>
            <input className="input" value={recipient.name} onChange={(e) => setRecipient((r) => ({ ...r, name: e.target.value }))} />
          </label>
          <label className="field">
            <span className="field__label">Email</span>
            <input className="input" type="email" value={recipient.email} onChange={(e) => setRecipient((r) => ({ ...r, email: e.target.value }))} placeholder="Leave blank to send later" />
            <span className="field__hint">The job's site contact by default. They get a link to review and sign.</span>
          </label>
        </div>
      )}
      <div className="form-grid">
        <label className="field">
          <span className="field__label">Your full name</span>
          <input className="input" required value={issuerName} onChange={(e) => setIssuerName(e.target.value)} autoComplete="name" />
          <span className="field__hint">Your signature on this SWMS, with the time, your IP address and browser.</span>
        </label>
      </div>
      <label className="check"><input type="checkbox" checked={issued} onChange={(e) => setIssued(e.target.checked)} /> {statement}</label>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>{busy ? "Making the SWMS" : toClient ? "Sign, create and send" : "Sign and create"}</button>
        <button type="button" className="btn btn--outline" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/** A SWMS version's client sign-off: status chip, and send, resend, cancel or open the executed copy. */
export function SwmsSignoff({ signoff, job, onChange }: { signoff: SwmsSignoffRow; job: JobDetail; onChange: () => void }) {
  const st = signoffState(signoff);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState({ name: st.last?.recipient_name ?? job.site_contact_name ?? "", email: st.last?.recipient_email ?? job.site_contact_email ?? "" });
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const canSend = Boolean(swmsTemplates()[0]?.clientSignoff);

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    try {
      ok(await fn());
      setSending(false);
      onChange();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: 6 }}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <span><Chip tone={st.tone === "ok" ? "green" : st.tone === "warn" ? "primary-outline" : "outline"}>{st.text}</Chip></span>
      <span className="sub">Issued by {signoff.issuer_name}, {signoff.issued_at_local}{st.live ? ` · link to ${st.live.recipient_email}` : ""}</span>
      <span className="actions">
        {signoff.client_signed_at && (
          <button type="button" className="btn btn--outline btn--xs" disabled={busy} onClick={() => void openBlobInNewTab(() => executedSwmsPdf(signoff.id)).catch(fail)}>Executed copy</button>
        )}
        {!signoff.client_signed_at && canSend && !sending && (
          <button type="button" className="btn btn--outline btn--xs" disabled={busy} onClick={() => setSending(true)}>{st.last ? "Send again" : "Send to client"}</button>
        )}
        {st.live && !signoff.client_signed_at && (
          <button type="button" className="btn btn--outline btn--xs" disabled={busy} onClick={() => void run(async () => { await cancelSwmsLink(st.live!.id); return "Link cancelled. It no longer works."; })}>Cancel link</button>
        )}
      </span>
      {sending && (
        <form
          className="stack"
          style={{ gap: 6 }}
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              const r = await sendSwmsLink(signoff.id, to.name.trim(), to.email.trim());
              return r.emailed ? `Sent to ${to.email.trim()}. Any earlier link no longer works.` : `Link made but the email failed: ${r.emailError}`;
            });
          }}
        >
          <input className="input" aria-label="Client name" value={to.name} onChange={(e) => setTo((t) => ({ ...t, name: e.target.value }))} placeholder="Name" />
          <input className="input" aria-label="Client email" type="email" required value={to.email} onChange={(e) => setTo((t) => ({ ...t, email: e.target.value }))} placeholder="Email" />
          <span className="actions">
            <button type="submit" className="btn btn--primary btn--xs" disabled={busy} aria-busy={busy}>Send</button>
            <button type="button" className="btn btn--outline btn--xs" disabled={busy} onClick={() => setSending(false)}>Cancel</button>
          </span>
        </form>
      )}
    </div>
  );
}

/** Today: jobs starting within 24 hours whose SWMS the client has not signed. A warning: crew can still work. */
export const swmsSignoffInUse = () => swmsTemplates().some((t) => t.clientSignoff);
export function SwmsDueCard() {
  const s = useAsync(() => unsignedSoon());
  const rows = s.data;
  return (
    <div className="card card--alert">
      <div className="card__head">
        <h2 className="card__title">SWMS not signed by the client</h2>
        <a className="card__link" href="#/jobs">Jobs <Icon name="arrow" size={16} /></a>
      </div>
      {s.loading && <SkeletonLines rows={2} />}
      {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
      {rows && rows.length === 0 && <EmptyState icon="check" title="All signed">Every job starting in the next 24 hours has a SWMS signed by the client.</EmptyState>}
      {rows && rows.length > 0 && (
        <>
          <p className="small muted" style={{ margin: "0 16px 8px" }}>Starting in the next 24 hours. Crew can still work; you decide.</p>
          <ul className="list">
            {rows.slice(0, 6).map((r) => (
              <li key={r.jobId} className="list__row">
                <span>
                  <a className="strong" href={`#/jobs/${r.jobId}`}>{r.reference} {r.title}</a>
                  <span className="sub">{r.reason}</span>
                </span>
                <span className="list__value list__value--primary">{fmtDay(r.firstShift)} {fmtTime(r.firstShift)}</span>
              </li>
            ))}
            {rows.length > 6 && <li className="list__more"><a href="#/jobs">{plural(rows.length - 6, "more job")}</a></li>}
          </ul>
        </>
      )}
    </div>
  );
}

/** Shown on the job while its current SWMS printed blank fields. */
export function SwmsMissingWarning({ documents }: { documents: JobDocument[] }) {
  const current = documents.filter((d) => !d.superseded_by && d.template_key && d.missing_fields?.length);
  if (!current.length) return null;
  return (
    <>
      {current.map((d) => (
        <p key={d.id} className="notice notice--warn small" role="status">
          {d.title}{d.version_label ? ` (${d.version_label})` : ""} printed "{swmsTemplates().find((t) => t.id === d.template_key)?.blankText ?? "Confirm at the toolbox talk"}" for the {listText(d.missing_fields!)}. Fill them in on the job and create a new version, or confirm them at the toolbox talk.
        </p>
      ))}
    </>
  );
}
